import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Linkedin, Mail, Globe, Loader2, CheckCircle2, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, breadcrumb } from "../hooks/useJsonLd";

const DAY_OPTIONS = ["Flexible — any day", "Today", "Tomorrow", "This week", "Next week", "In 2+ weeks"];
const TIME_OPTIONS = ["Flexible — any time", "Morning (9 AM – 12 PM)", "Afternoon (12 – 4 PM)", "Evening (4 – 8 PM)"];
const TZ_OPTIONS = ["IST (India)", "GMT (UK)", "CET (Europe)", "EST (US East)", "PST (US West)", "GST (UAE)", "SGT (Singapore)", "Other"];

export default function Contact() {
  useSEO({
    title: "Book a Growth Audit · Contact Shivani Ranawat",
    description: "Marketing isn't about spending more — it's about converting better. Book a 30-minute growth audit or send a message and I'll respond within 24 hours.",
    path: "/contact",
  });
  useJsonLd([
    breadcrumb([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Shivani Ranawat",
      description: "Book a strategy call or send a message.",
      mainEntity: { "@id": "https://marketing-ops-18.emergent.host/#shivani" },
    },
  ]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    preferred_day: "",
    preferred_time: "",
    timezone: "IST (India)",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    try {
      setStatus("loading");
      await submitContact(form);
      setStatus("success");
      toast.success("Message sent. I'll confirm your slot within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "", preferred_day: "", preferred_time: "", timezone: "IST (India)" });
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0F1115] text-white mesh-bg grain relative">
      <div className="relative pt-32 pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/40">16 — Let&apos;s Build</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl"
          >
            Marketing isn&apos;t about<br /> spending more.<br />
            <span className="text-[#3B82F6]">It&apos;s about converting better.</span>
          </motion.h1>

          <div className="mt-16 grid lg:grid-cols-12 gap-10">
            {/* Contact info */}
            <div className="lg:col-span-5">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-6">Direct Channels</div>

              <div className="space-y-3">
                <a data-testid="contact-linkedin" href="https://www.linkedin.com/in/shivani-ranawat-a16929122/" target="_blank" rel="noreferrer" className="group flex items-center justify-between rounded-2xl glass-dark p-5 hover-glow">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/15 text-[#3B82F6] flex items-center justify-center">
                      <Linkedin size={18} />
                    </div>
                    <div>
                      <div className="font-display text-lg">LinkedIn</div>
                      <div className="text-xs text-white/50">linkedin.com/in/shivani-ranawat-a16929122</div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition" />
                </a>

                <a data-testid="contact-email" href="mailto:shivaniranawatdigitalworld@gmail.com" className="group flex items-center justify-between rounded-2xl glass-dark p-5 hover-glow">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 text-[#10B981] flex items-center justify-center">
                      <Mail size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg">Email</div>
                      <div className="text-xs text-white/50 truncate">shivaniranawatdigitalworld@gmail.com</div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition shrink-0" />
                </a>

                <a data-testid="contact-portfolio" href="/" className="group flex items-center justify-between rounded-2xl glass-dark p-5 hover-glow">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 text-white flex items-center justify-center">
                      <Globe size={18} />
                    </div>
                    <div>
                      <div className="font-display text-lg">Portfolio</div>
                      <div className="text-xs text-white/50">shivaniranawat.com</div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition" />
                </a>
              </div>

              {/* QR + Meta */}
              <div className="mt-8 flex items-center gap-5 glass-dark rounded-2xl p-5">
                <div className="w-24 h-24 rounded-xl bg-white p-2 flex items-center justify-center">
                  {/* Simple SVG QR aesthetic */}
                  <svg viewBox="0 0 40 40" className="w-full h-full text-black">
                    {Array.from({ length: 8 }).map((_, y) =>
                      Array.from({ length: 8 }).map((_, x) => (
                        (x + y) % 3 === 0 || (x * y) % 5 === 0
                          ? <rect key={`${x}-${y}`} x={x * 5} y={y * 5} width="4" height="4" fill="currentColor" />
                          : null
                      ))
                    )}
                    <rect x="0" y="0" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="28" y="0" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="0" y="28" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">Scan to save contact</div>
                  <div className="mt-2 font-display text-lg">The Growth Systems Portfolio</div>
                  <div className="text-xs text-white/50">v.2026.01</div>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-7">
              <div className="glass-dark rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">Book a Growth Audit</div>
                  <div className="text-xs font-mono text-[#10B981] inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] pulse-dot" /> Response in 24h
                  </div>
                </div>

                <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">Full name</label>
                      <input
                        data-testid="contact-name"
                        value={form.name}
                        onChange={onChange("name")}
                        required
                        placeholder="Jane Doe"
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">Email</label>
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        required
                        placeholder="jane@company.com"
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">Company</label>
                    <input
                      data-testid="contact-company"
                      value={form.company}
                      onChange={onChange("company")}
                      placeholder="Company (optional)"
                      className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
                    />
                  </div>

                  {/* Pick a time */}
                  <div data-testid="pick-a-time" className="rounded-xl border border-[#3B82F6]/25 bg-[#3B82F6]/[0.06] p-4 md:p-5">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#93C5FD] mb-3">
                      <Calendar size={12} /> Pick a time · optional
                    </div>
                    <div className="text-sm text-white/80 mb-4">
                      Prefer a specific slot? Tell me when — I&apos;ll confirm within 24 hours.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1.5 block">Day</label>
                        <select
                          data-testid="contact-preferred-day"
                          value={form.preferred_day}
                          onChange={onChange("preferred_day")}
                          className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-3 py-2.5 text-sm text-white"
                        >
                          <option value="">Select day</option>
                          {DAY_OPTIONS.map((d) => <option key={d} value={d} className="bg-[#0F1115]">{d}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1.5 block">Time window</label>
                        <select
                          data-testid="contact-preferred-time"
                          value={form.preferred_time}
                          onChange={onChange("preferred_time")}
                          className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-3 py-2.5 text-sm text-white"
                        >
                          <option value="">Select time</option>
                          {TIME_OPTIONS.map((t) => <option key={t} value={t} className="bg-[#0F1115]">{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1.5 block">Timezone</label>
                        <select
                          data-testid="contact-timezone"
                          value={form.timezone}
                          onChange={onChange("timezone")}
                          className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-3 py-2.5 text-sm text-white"
                        >
                          {TZ_OPTIONS.map((z) => <option key={z} value={z} className="bg-[#0F1115]">{z}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-[11px] text-white/50">
                      <Clock size={12} /> 30-minute strategy calls · complimentary
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">What are you trying to grow?</label>
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={onChange("message")}
                      required
                      rows={5}
                      placeholder="Where you are, where you want to go, and what you've already tried."
                      className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30 resize-none"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="text-xs text-white/40">By submitting, you agree to be contacted about growth engagements.</div>
                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 px-6 py-3 text-sm font-medium hover:bg-white/90 transition disabled:opacity-60"
                    >
                      {status === "loading" && <Loader2 size={14} className="animate-spin" />}
                      {status === "success" && <CheckCircle2 size={14} className="text-[#10B981]" />}
                      {status === "loading" ? "Sending…" : status === "success" ? "Sent" : "Send Message"}
                      {status !== "loading" && status !== "success" && <ArrowRight size={14} />}
                    </button>
                  </div>
                </form>
              </div>

              <div className="mt-4 text-[11px] font-mono text-white/40 text-right">
                * Preferred for teams with USD 5K+/mo ad spend or planning a Q2 launch.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
